import { lazy, Suspense, type ReactNode } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { useAppStore } from "@/store/useAppStore";
import { Toaster } from "@/components/ui/toaster";
import { AccessCodeWalletGate } from "@/components/auth/AccessCodeWalletGate";
import { WalletGate } from "@/components/auth/WalletGate";
import { ReferralCodePrompt } from "@/components/referral/ReferralCodePrompt";
import {
  REQUIRE_ACCESS_CODE,
  ENABLE_ACCESS_CONTROL,
  ENABLE_RISK_DASHBOARD,
  ENABLE_POINTS_REFERRAL,
} from "@/config/constants";

const NotFoundPage = lazy(() =>
  import("@/pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })),
);
const SwapPage = lazy(() =>
  import("@/pages/SwapPage").then((m) => ({ default: m.SwapPage })),
);
const PoolsPage = lazy(() =>
  import("@/pages/PoolsPage").then((m) => ({ default: m.PoolsPage })),
);
const PoolPage = lazy(() =>
  import("@/pages/PoolPage").then((m) => ({ default: m.PoolPage })),
);
const PortfolioPage = lazy(() =>
  import("@/pages/PortfolioPage").then((m) => ({ default: m.PortfolioPage })),
);
const BridgePage = lazy(() =>
  import("@/pages/BridgePage").then((m) => ({ default: m.BridgePage })),
);
const PointsPage = lazy(() =>
  import("@/pages/PointsPage").then((m) => ({ default: m.PointsPage })),
);
const ReferralPage = lazy(() =>
  import("@/pages/ReferralPage").then((m) => ({ default: m.ReferralPage })),
);
const TermsOfUsePage = lazy(() =>
  import("@/pages/TermsOfUsePage").then((m) => ({
    default: m.TermsOfUsePage,
  })),
);
const PrivacyPolicyPage = lazy(() =>
  import("@/pages/PrivacyPolicyPage").then((m) => ({
    default: m.PrivacyPolicyPage,
  })),
);
const DisclaimerPage = lazy(() =>
  import("@/pages/DisclaimerPage").then((m) => ({
    default: m.DisclaimerPage,
  })),
);
const RiskDashboardPage = ENABLE_RISK_DASHBOARD
  ? lazy(() =>
      import("@/pages/admin/RiskDashboardPage").then((m) => ({
        default: m.RiskDashboardPage,
      })),
    )
  : null;

function RouteFallback() {
  return (
    <div className="flex min-h-[12rem] items-center justify-center text-sm text-muted-foreground">
      Loading...
    </div>
  );
}

function lazyRoute(element: ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

function GatedRoutes() {
  const gatedContent = (
    <Layout>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<SwapPage />} />
          <Route path="/swap" element={<Navigate to="/" replace />} />
          <Route path="/pools" element={<PoolsPage />} />
          <Route path="/pools/:poolId" element={<PoolPage />} />
          <Route path="/pools/:poolId/:tab" element={<PoolPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          {ENABLE_POINTS_REFERRAL && (
            <>
              <Route path="/points" element={<PointsPage />} />
              <Route path="/referral" element={<ReferralPage />} />
            </>
          )}
          <Route path="/bridge" element={<BridgePage />} />
          {RiskDashboardPage && (
            <Route path="/-/admin/*" element={<RiskDashboardPage />} />
          )}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </Layout>
  );

  if (REQUIRE_ACCESS_CODE) {
    return <AccessCodeWalletGate>{gatedContent}</AccessCodeWalletGate>;
  }

  if (ENABLE_ACCESS_CONTROL) {
    return <WalletGate>{gatedContent}</WalletGate>;
  }

  return gatedContent;
}

function App() {
  useAppStore();

  return (
    <Router>
      <Routes>
        <Route
          path="/terms"
          element={
            <Layout showHeader={false}>{lazyRoute(<TermsOfUsePage />)}</Layout>
          }
        />
        <Route
          path="/privacy"
          element={
            <Layout showHeader={false}>
              {lazyRoute(<PrivacyPolicyPage />)}
            </Layout>
          }
        />
        <Route
          path="/disclaimer"
          element={
            <Layout showHeader={false}>{lazyRoute(<DisclaimerPage />)}</Layout>
          }
        />
        <Route path="/*" element={<GatedRoutes />} />
      </Routes>
      {ENABLE_POINTS_REFERRAL && <ReferralCodePrompt />}
      <Toaster />
    </Router>
  );
}

export default App;
